import axios from 'axios';
import {
  PagarmeSubscriptionRequest,
  PagarmeSubscriptionResponse,
  SubscriptionFormData,
  ApiResponse,
  PagarmeCustomer
} from '../types';
import { logError } from '../utils/logger';

// Configuração da API do Backend
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

// Cliente Axios configurado para o Backend
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000
});

/**
 * Cria uma assinatura avulsa no Pagar.me
 * Implementa o endpoint: POST /subscriptions
 * Documentação: https://docs.pagar.me/reference/criar-assinatura-avulsa
 * 
 * IMPORTANTE: Em produção, esta chamada deve ser feita através do backend
 * Os dados do cartão já foram tokenizados e não estão incluídos aqui
 * 
 * @param subscriptionData - Dados da assinatura com token do cartão
 * @returns Resposta da API
 */
export const createSubscription = async (
  subscriptionData: PagarmeSubscriptionRequest
): Promise<ApiResponse<PagarmeSubscriptionResponse>> => {
  const startTime = Date.now();
  
  try {
    const response = await apiClient.post<any>(
      '/subscriptions',
      subscriptionData
    );
    
    const duration = Date.now() - startTime;
    // Backend retorna: {success: true, data: PagarmeSubscriptionResponse}
    const subscription = response.data?.data;
    
    return {
      success: true,
      data: subscription
    };
  } catch (error: any) {
    const duration = Date.now() - startTime;
    
    logError('Erro ao criar assinatura', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      duration_ms: duration,
    });
    
    return {
      success: false,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Busca as cobranças (charges) de uma assinatura
 * Usado para pegar URL do boleto após criar assinatura
 * 
 * @param subscriptionId - ID da assinatura
 * @returns Lista de cobranças
 */
export const fetchSubscriptionCharges = async (
  subscriptionId: string
): Promise<ApiResponse<any>> => {
  try {
    const response = await apiClient.get<any>(
      `/subscriptions/${subscriptionId}/charges`
    );
    
    const charges = response.data?.data;
    
    return {
      success: true,
      data: charges
    };
  } catch (error: any) {
    logError('Erro ao buscar cobranças', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      subscription_id: subscriptionId
    });
    
    return {
      success: false,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Formata os dados do formulário para o padrão da API Pagar.me
 * Suporta múltiplos métodos de pagamento: Cartão, PIX e Boleto
 * 
 * @param formData - Dados do formulário
 * @param cardToken - Token do cartão (obrigatório apenas para credit_card)
 * @returns Dados formatados para a API
 */
export const formatSubscriptionData = (
  formData: SubscriptionFormData,
  cardToken?: string
): PagarmeSubscriptionRequest => {
  const customer: PagarmeCustomer = {
    name: formData.customerName,
    email: formData.customerEmail,
    document: formData.customerDocument.replace(/\D/g, ''),
    type: formData.customerDocument.replace(/\D/g, '').length === 11 ? 'individual' : 'company',
    phones: {
      mobile_phone: {
        country_code: '55',
        area_code: formData.phoneAreaCode,
        number: formData.phoneNumber.replace(/\D/g, '')
      }
    },
    address: {
      line_1: `${formData.addressNumber}, ${formData.addressStreet}, ${formData.addressNeighborhood}`,
      line_2: formData.addressComplement || '',
      zip_code: formData.addressZipCode.replace(/\D/g, ''),
      city: formData.addressCity,
      state: formData.addressState,
      country: 'BR'
    }
  };

  const baseRequest: PagarmeSubscriptionRequest = {
    customer,
    plan_id: formData.planId,
    payment_method: formData.paymentMethod,
    metadata: {
      source: 'itauna-web',
      created_at: new Date().toISOString(),
      type: 'avulsa'
    }
  };

  // Adiciona configurações específicas por método de pagamento
  if (formData.paymentMethod === 'credit_card') {
    if (!cardToken) {
      throw new Error('Token do cartão é obrigatório para pagamento com cartão de crédito');
    }
    baseRequest.card_token = cardToken;
    baseRequest.metadata!.tokenized = true;
    baseRequest.card = {
      billing_address: {
        line_1: `${formData.addressNumber}, ${formData.addressStreet}, ${formData.addressNeighborhood}`,
        line_2: formData.addressComplement || '',
        zip_code: formData.addressZipCode.replace(/\D/g, ''),
        city: formData.addressCity,
        state: formData.addressState,
        country: 'BR'
      }
    };

    // Envia installments quando o plano permite parcelamento
    if (formData.installments && formData.installments > 0) {
      baseRequest.installments = formData.installments;
      baseRequest.metadata!.installments = formData.installments;
    }
  } else if (formData.paymentMethod === 'boleto') {
    baseRequest.metadata!.payment_type = 'boleto';
  }

  return baseRequest;
};

/**
 * Valida CPF
 * @param cpf - CPF para validação
 * @returns true se válido, false se inválido
 */
export const validateCPF = (cpf: string): boolean => {
  const cleanCpf = cpf.replace(/\D/g, '');
  
  if (cleanCpf.length !== 11 || /^(\d)\1+$/.test(cleanCpf)) {
    return false;
  }

  let sum = 0;
  let remainder: number;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleanCpf.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleanCpf.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.substring(10, 11))) return false;

  return true;
};

/**
 * Valida CNPJ
 * @param cnpj - CNPJ para validação
 * @returns true se válido, false se inválido
 */
export const validateCNPJ = (cnpj: string): boolean => {
  const cleanCnpj = cnpj.replace(/\D/g, '');

  if (cleanCnpj.length !== 14 || /^(\d)\1+$/.test(cleanCnpj)) {
    return false;
  }

  let size = cleanCnpj.length - 2;
  let numbers = cleanCnpj.substring(0, size);
  const digits = cleanCnpj.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  size = size + 1;
  numbers = cleanCnpj.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;

  return true;
};

export default {
  createSubscription,
  formatSubscriptionData,
  validateCPF,
  validateCNPJ
};
